import {
	CameraOutlined,
	CloseOutlined,
	PlusOutlined,
	QuestionCircleOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Button,
	Divider,
	Form,
	Image,
	Input,
	Modal,
	Select,
	Upload,
	message,
	Tooltip,
} from "antd";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import { motion } from "framer-motion";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import EventSettingsDetail from "views/Admin/Categories/AddEdit/EventSettingsDetail";
import PublicStatus from "views/Admin/Categories/AddEdit/PublicStatus";
import TagInput from "views/Admin/Categories/AddEdit/TagInput";

const CustomUpload = styled(Upload)`
	.ant-upload {
		width: 100%;
		max-height: "200px";
	}
`;

const { Option } = Select;
const { TextArea } = Input;

const OccasionModal = (props) => {
	const {
		isOccasionModalVisible,
		hideOccasionModal,
		currentOccasion,
		currentCategory,
		publicSettings,
	} = props;

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [occasionForm] = Form.useForm();
	const [occasionImages, setOccasionImages] = useState([]);
	const [templates, setTemplates] = useState([]);
	const [currentTemplateOccasion, setCurrentTemplateOccasion] =
		useState(undefined);
	const [emailsLineWorks, setEmailsLineWorks] = useState([]);
	const tagInputRef = useRef();

	useQuery(
		[API.QUERY_KEY_ADMIN_OCCASION_TEMPLATES],
		() => API.ADMIN_GET_OCCASION_TEMPLATES(),
		{
			enabled: isOccasionModalVisible,
			onSuccess: (response) => {
				if (isMountedRef.current) {
					setTemplates(response?.data || []);
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	useQuery(
		[API.QUERY_KEY_ADMIN_OCCASION_TEMPLATE_DETAIL, currentTemplateOccasion],
		() => API.ADMIN_GET_OCCASION_TEMPLATE_DETAIL(currentTemplateOccasion),
		{
			enabled: isOccasionModalVisible && !!currentTemplateOccasion,
			onSuccess: (response) => {
				if (isMountedRef.current) {
					if (response?.data) {
						setOccasionImages([]);

						if (
							response?.data?.message &&
							Array.isArray(JSON.parse(response?.data?.message)) &&
							currentCategory?.categoryMessages.length > 0
						) {
							JSON.parse(response?.data?.message).forEach((item) => {
								const index = currentCategory?.categoryMessages.findIndex(
									(i) => i.label === item,
								);
								occasionForm.setFieldValue(`label${index}`, true);
							});
						}

						occasionForm.setFieldsValue({
							isDisplayed:
								response?.data?.isDisplayed !== undefined
									? response?.data.isDisplayed + ""
									: undefined,
							title: response?.data?.title,
							occasionImages: [{}],
							description: response?.data?.description,
							occasionDetails: response?.data?.occasionDetails || [],
							isSettingTime: response?.data?.isSettingTime,
							...(response?.data?.isSettingTime
								? {
										startRegistration: moment(
											response?.data?.startRegistration,
										),
										endRegistration: moment(response?.data?.endRegistration),
										startDate: moment(response?.data?.startDate),
										endDate: moment(response?.data?.endDate),
										numberOfPeople: response?.data?.numberOfPeople,
									}
								: {
										notRegisterEventSameTime:
											response?.data?.notRegisterEventSameTime,
									}),
							isMultiEvent: response?.data?.isMultiEvent,
							isMessage: response?.data?.isMessage,
							CategoryMessageDetail: response?.data?.CategoryMessageDetail,
							messageRegisteredLineWorks:
								response?.data?.CategoryMessageDetail
									?.messageRegisteredLineWorks,
							messageCanceledLineWorks:
								response?.data?.CategoryMessageDetail?.messageCanceledLineWorks,
						});
						setEmailsLineWorks(
							response?.data?.emailsLineWorks?.split(",")?.filter(Boolean) ||
								[],
						);
					}
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	const createOccasionMutation = useMutation(API.ADMIN_CREATE_OCCASION, {
		onSuccess: (response) => {
			if (isMountedRef.current) {
				message.success(COMMONS.SUCCESS_CREATE_MSG);
				queryClient.invalidateQueries({
					queryKey: [API.QUERY_KEY_ADMIN_OCCASIONS],
				});
				hideOccasionModal();
			}
		},
		onError: (error) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning({
					content: COMMONS.ERROR_SESSION_MSG,
					key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				const errorMessage = error?.response?.data?.errors?.[0]?.message;

				message.error({
					content: errorMessage ?? COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		},
	});

	const updateOccasionMutation = useMutation(API.ADMIN_UPDATE_OCCASION, {
		onSuccess: async (_, data) => {
			if (isMountedRef.current) {
				message.success(COMMONS.SUCCESS_UPDATE_MSG);
				queryClient.invalidateQueries({
					queryKey: [API.QUERY_KEY_ADMIN_OCCASIONS],
				});
				if (data.occasionId) {
					await queryClient.invalidateQueries([
						API.QUERY_KEY_ADMIN_OCCASION_DETAIL,
					]);
				}
				hideOccasionModal();
			}
		},
		onError: (error) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning({
					content: COMMONS.ERROR_SESSION_MSG,
					key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				const errorMessage = error?.response?.data?.errors?.[0]?.message;

				message.error({
					content: errorMessage ?? COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		},
	});

	useEffect(() => {
		if (!isOccasionModalVisible) {
			occasionForm.resetFields();
			setOccasionImages([]);
			setTemplates([]);
			setCurrentTemplateOccasion(undefined);
			setEmailsLineWorks([]);
		}
	}, [isOccasionModalVisible, occasionForm]);

	useEffect(() => {
		if (isOccasionModalVisible && currentOccasion) {
			setOccasionImages(currentOccasion?.occasionImages || []);

			if (
				currentOccasion?.message &&
				Array.isArray(JSON.parse(currentOccasion?.message)) &&
				currentCategory?.categoryMessages.length > 0
			) {
				JSON.parse(currentOccasion?.message).forEach((item) => {
					const index = currentCategory?.categoryMessages.findIndex(
						(i) => i.label === item,
					);
					occasionForm.setFieldValue(`label${index}`, true);
				});
			}

			occasionForm.setFieldsValue({
				isDisplayed:
					currentOccasion?.isDisplayed !== undefined
						? currentOccasion.isDisplayed + ""
						: undefined,
				title: currentOccasion?.title,
				occasionImages: currentOccasion?.occasionImages
					? currentOccasion.occasionImages.map((image) => {
							return { image: image?.picUrl };
						})
					: [{}],
				description: currentOccasion?.description,
				occasionDetails: currentOccasion?.occasionDetails || [],
				cancelable: currentOccasion?.cancelable,
				cancelDescription: currentOccasion?.cancelDescription || "",
				fee: currentOccasion?.fee,
				cancelConditions: currentOccasion?.cancelConditions || [],

				isSettingTime: currentOccasion?.isSettingTime,
				...(currentOccasion?.isSettingTime
					? {
							startRegistration: moment(currentOccasion?.startRegistration),
							endRegistration: moment(currentOccasion?.endRegistration),
							startDate: moment(currentOccasion?.startDate),
							endDate: moment(currentOccasion?.endDate),
							numberOfPeople: currentOccasion?.numberOfPeople,
						}
					: {
							notRegisterEventSameTime:
								currentOccasion?.notRegisterEventSameTime,
						}),
				isMultiEvent: currentOccasion?.isMultiEvent,
				isMessage: currentOccasion.isMessage,
				CategoryMessageDetail: currentOccasion.CategoryMessageDetail,
				messageRegisteredLineWorks:
					currentOccasion?.CategoryMessageDetail?.messageRegisteredLineWorks,
				messageCanceledLineWorks:
					currentOccasion?.CategoryMessageDetail?.messageCanceledLineWorks,
			});

			setEmailsLineWorks(currentOccasion?.emailsLineWorks?.split(","));
		}
		// eslint-disable-next-line
	}, [isOccasionModalVisible, currentOccasion]);

	const invalidEmailLineWorks = () => {
		const finalEmails = getFinalEmailsLineWorks();
		if (finalEmails.length > 10) {
			message.error(COMMONS.MESSAGE_VALIDATION.LINE_ID_OVER_10);
			return true;
		}
		return false;
	};

	const getFinalEmailsLineWorks = () => {
		const inputValue = tagInputRef.current?.getCurrentInput()?.trim();
		const emails = [...(emailsLineWorks || [])];

		if (inputValue && !emails.includes(inputValue)) {
			emails.push(inputValue);
		}

		return emails;
	};

	const handleOccasion = async () => {
		try {
			await occasionForm.validateFields();
			const finalEmailsLineWorksResult = getFinalEmailsLineWorks();

			if (finalEmailsLineWorksResult.length > 10) {
				message.error(COMMONS.MESSAGE_VALIDATION.LINE_ID_OVER_10);
				return;
			}

			tagInputRef.current.confirmInput();

			const data = occasionForm.getFieldsValue();

			const formData = new FormData();
			let notes;
			if (currentCategory?.categoryMessages) {
				notes = currentCategory?.categoryMessages
					?.filter((_, index) => data[`label${index}`])
					.map((item) => item.label);
			}

			formData.append("message", notes ? JSON.stringify(notes) : []);

			formData.append("fee", data.fee || 0);
			formData.append("cancelable", data.cancelable || false);
			formData.append(
				"cancelConditions",
				JSON.stringify(data.cancelConditions || []),
			);
			formData.append(
				"cancelDescription",
				data?.cancelDescription?.trim() || "",
			);

			formData.append("isDisplayed", data?.isDisplayed + "");
			formData.append("title", data?.title?.trim() || "");
			formData.append("description", data?.description?.trim() || "");
			formData.append("groupBooking", data?.groupBooking || false);
			formData.append("isMessage", data.isMessage || false);

			formData.append(
				"categoryMessageDetail",
				JSON.stringify(
					COMMONS.trimStrings({
						...(data?.categoryMessageDetail || {}),
						messageRegisteredLineWorks: data?.messageRegisteredLineWorks ?? "",
						messageCanceledLineWorks: data?.messageCanceledLineWorks ?? "",
					}),
				),
			);

			if (finalEmailsLineWorksResult.length > 0) {
				formData.append(
					"emailsLineWorks",
					JSON.stringify(finalEmailsLineWorksResult),
				);
			}

			if (data?.isSettingTime) {
				formData.append(
					"startRegistration",
					moment(data?.startRegistration).toISOString(),
				);
				formData.append(
					"endRegistration",
					moment(data?.endRegistration).toISOString(),
				);
				formData.append("startDate", moment(data?.startDate).toISOString());
				formData.append("endDate", moment(data?.endDate).toISOString());
				formData.append("numberOfPeople", Number(data?.numberOfPeople));
				formData.append("isSendImage", data?.isSendImage || false);
			} else {
				if (data?.isMultiEvent) {
					formData.append(
						"notRegisterEventSameTime",
						data?.notRegisterEventSameTime || false,
					);
				}
			}

			formData.append("isMultiEvent", data?.isMultiEvent || true);
			formData.append("isSettingTime", data?.isSettingTime || false);

			if (data?.occasionDetails && data?.occasionDetails?.length > 0) {
				formData.append(
					"occasionDetails",
					JSON.stringify(
						data.occasionDetails.map((od, i) => ({ ...od, showOrder: i })),
					),
				);
			} else {
				formData.append("occasionDetails", "");
			}

			if (occasionImages && occasionImages.length > 0) {
				formData.append(
					"occasionImagesData",
					JSON.stringify(
						occasionImages.map((image, i) => ({
							originalName: image?.file?.name || image?.picUrl,
							showOrder: i,
						})),
					),
				);

				occasionImages
					.filter((image) => image?.file)
					.map((image) => image?.file)
					.forEach((imageData) => {
						formData.append("occasionImages", imageData);
					});
			} else {
				formData.append("occasionImages", "");
				formData.append("occasionImagesData", "");
			}

			let paramData = {
				formData: formData,
			};

			if (currentOccasion) {
				paramData.occasionId = currentOccasion?.occasionId;

				updateOccasionMutation.mutate(paramData);
			} else {
				formData.append("categoryId", currentCategory?.categoryId);

				createOccasionMutation.mutate(paramData);
			}
		} catch (e) {
			console.log("error: ", e);
		}
	};

	const handleTemplateChange = (occasionId) => {
		setCurrentTemplateOccasion(occasionId);
	};

	const occasionImagesOnDragEndHandle = (result) => {
		if (!result.destination) {
			return;
		} else {
			const orderedOccasionImages = COMMONS.RE_ORDER(
				occasionForm.getFieldValue("occasionImages"),
				result.source.index,
				result.destination.index,
			);

			const orderedOccasionImageFiles = COMMONS.RE_ORDER(
				occasionImages,
				result.source.index,
				result.destination.index,
			);

			occasionForm.setFieldsValue({
				occasionImages: orderedOccasionImages,
			});

			setOccasionImages(orderedOccasionImageFiles);
		}
	};

	// const occasionDetailsOnDragEndHandle = (result) => {
	//   if (!result.destination) {
	//     return
	//   } else {
	//     const orderedOccasionDetails = COMMONS.RE_ORDER(
	//       occasionForm.getFieldValue("occasionDetails"),
	//       result.source.index,
	//       result.destination.index
	//     )

	//     occasionForm.setFieldsValue({
	//       occasionDetails: orderedOccasionDetails,
	//     })
	//   }
	// }

	return (
		<>
			<Modal
				open={isOccasionModalVisible}
				onCancel={hideOccasionModal}
				title={
					currentOccasion
						? `${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム編集`
						: `新規${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム`
				}
				footer={null}
				maskClosable={false}
				centered
				width={720}
				styles={{
					body: {
						maxHeight: "90vh",
						overflowY: "auto",
						overflowX: "hidden",
					},
				}}
			>
				<div className="p-2">
					<Form
						size="large"
						form={occasionForm}
						layout="vertical"
						initialValues={{
							isDisplayed: false,
							occasionImages: [{}],
							occasionDetails: [{}],
							isMultiEvent: true,
							notRegisterEventSameTime: false,
							fee: 0,
							cancelable: false,
							cancelConditions: [
								{
									name: "状態 1",
									day: 0,
									hour: 18,
									minute: 0,
									refundPercentage: 0,
								},
							],
						}}
						requiredMark={false}
						scrollToFirstError={COMMONS.FORM_SCROLL_CONFIG}
					>
						<motion.div
							variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
							initial="hidden"
							animate="show"
							exit="hidden"
						>
							<motion.div
								variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
								className="flex justify-end mb-4"
							>
								<Select
									placeholder="既存のイベント情報を取得"
									onChange={handleTemplateChange}
									style={{ width: "300px", textAlign: "center" }}
								>
									{templates.map((template) => (
										<Option
											key={template?.occasionId}
											value={template?.occasionId}
										>
											{(template?.title || "ー") + "のテンプレート"}
										</Option>
									))}
								</Select>
							</motion.div>

							<PublicStatus />

							<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
								<Form.Item
									name="title"
									label={
										<p>
											<span className="font-bold">{`${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム`}</span>
											<Tooltip
												title={
													<div>
														最大255文字、プログラム一覧ページで表示できるのは約17文字です。
													</div>
												}
												placement="bottom"
												overlayInnerStyle={{
													width: 370,
													backgroundColor: "#000000",
												}}
											>
												<QuestionCircleOutlined
													className="ml-2 mr-4"
													style={{
														color: publicSettings?.PRIMARY_COLOR,
													}}
												/>
											</Tooltip>
										</p>
									}
									rules={[
										{
											required: true,
											whitespace: true,
											message: `${COMMONS.DEFAULT_SYSTEM_TYPE}タイトルは必須です`,
										},
										{
											max: 255,
											message: "イベントプログラムは255文字以内にしてください",
										},
									]}
								>
									<Input
										placeholder={`${COMMONS.DEFAULT_SYSTEM_TYPE}プログラムを入力してください`}
										allowClear
									/>
								</Form.Item>
							</motion.div>

							<Divider />

							<EventSettingsDetail />

							<Divider />

							<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
								<p>
									<span className="font-bold">{`${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム画像`}</span>
								</p>
								<p className="text-xs text-gray-400 mb-4 mt-2">
									※項目をドラッグして表示順を自由に調整できます
								</p>
								<div>
									<Form.List name="occasionImages">
										{(fields, { add, remove }) => (
											<DragDropContext
												onDragEnd={occasionImagesOnDragEndHandle}
											>
												<Droppable
													droppableId="droppableOccasionImage"
													direction="vertical"
												>
													{(provided, snapshot) => (
														<div
															ref={provided.innerRef}
															{...provided.droppableProps}
															className="w-full"
														>
															{fields.map((field, index) => (
																<Draggable
																	key={field.key + ""}
																	draggableId={field.key + ""}
																	index={index}
																>
																	{(provided, snapshot) => (
																		<div
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																			className="border border-dashed border-gray-300 rounded bg-white p-4 mb-4"
																		>
																			<Form.Item
																				{...field}
																				key={[field.key, "image"]}
																				name={[field.name, "image"]}
																				label={
																					<p>
																						<span>{`${
																							COMMONS.DEFAULT_SYSTEM_TYPE
																						}プログラム画像（${
																							index + 1
																						}）`}</span>
																					</p>
																				}
																				valuePropName="file"
																				className="mb-0 text-center"
																				rules={[
																					{
																						required: true,
																						message: `${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム画像をアプロードしてください`,
																					},
																				]}
																			>
																				<span>
																					<CustomUpload
																						accept=".jpg, .jpeg, .png"
																						maxCount={1}
																						showUploadList={false}
																						beforeUpload={() => {
																							return false;
																						}}
																						onChange={async (param) => {
																							const _file =
																								await COMMONS.RESIZE_FILE(
																									param?.file,
																									"file",
																								);
																							const _preview =
																								await COMMONS.RESIZE_FILE(
																									param?.file,
																									"base64",
																								);

																							if (occasionImages[index]) {
																								setOccasionImages(
																									occasionImages.map((ci, i) =>
																										i === index
																											? {
																													file: _file,
																													preview: _preview,
																												}
																											: ci,
																									),
																								);
																							} else {
																								let duplicateArray = [
																									...occasionImages,
																								];
																								duplicateArray[index] = {
																									file: _file,
																									preview: _preview,
																								};

																								setOccasionImages(
																									duplicateArray,
																								);
																							}
																						}}
																					>
																						<TapAnimationComponent>
																							{occasionImages[index] &&
																							(occasionImages[index]?.preview ||
																								occasionImages[index]
																									?.picUrl) ? (
																								<div className="flex justify-center">
																									<Image
																										preview={false}
																										src={
																											occasionImages[index]
																												?.preview ||
																											`${API.OCCASIONS_UPLOADS_URL}${occasionImages[index]?.picUrl}`
																										}
																										alt={`${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム画像`}
																										fallback="/no-image.png"
																										style={{
																											maxHeight: "250px",
																										}}
																										className="max-w-full cursor-pointer object-contain"
																									/>
																								</div>
																							) : (
																								<div
																									className="flex justify-center items-center bg-white border border-gray-300 rounded w-full cursor-pointer"
																									style={{
																										height: "250px",
																										maxHeight: "250px",
																									}}
																								>
																									<p className="text-center text-2xl font-bold">
																										<CameraOutlined className="mr-2" />
																									</p>
																								</div>
																							)}
																						</TapAnimationComponent>
																					</CustomUpload>
																				</span>
																			</Form.Item>
																			{fields.length > 1 ? (
																				<Form.Item
																					{...field}
																					className="text-right"
																				>
																					<Button
																						size="default"
																						danger
																						onClick={() => {
																							remove(field.name);
																							setOccasionImages(
																								occasionImages.filter(
																									(ci, i) => i !== index,
																								),
																							);
																						}}
																						icon={<CloseOutlined />}
																					>
																						削除する
																					</Button>
																				</Form.Item>
																			) : null}
																		</div>
																	)}
																</Draggable>
															))}
															{provided.placeholder}
															<Form.Item>
																<Button
																	type="link"
																	onClick={() => add()}
																	block
																	icon={<PlusOutlined className="mr-1" />}
																>
																	{`${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム画像追加`}
																</Button>
															</Form.Item>
														</div>
													)}
												</Droppable>
											</DragDropContext>
										)}
									</Form.List>
								</div>
							</motion.div>

							<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
								<Form.Item
									name="description"
									label={
										<p className="font-bold">{`${COMMONS.PAGE_ADMIN_LIST_EVENTS}内容`}</p>
									}
								>
									<Input.TextArea
										placeholder="例：○○"
										autoSize={{ minRows: 3 }}
									/>
								</Form.Item>
							</motion.div>

							<motion.div
								variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
								className="font-bold text-[#262626]"
							>
								管理者通知設定（LINE WORKS）
							</motion.div>
							<motion.div
								variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
								className="p-4 bg-yellow-300 rounded my-4"
							>
								<div className="whitespace-pre-line text-center">
									{`管理者通知先は下記優先度で採用されます。\nシステム全体の設定＜親イベントの設定＜イベントプログラムの設定`}
								</div>
							</motion.div>
							<motion.div
								variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
								className="mb-4"
							>
								<p className="whitespace-pre-line">
									{`※メッセージに[BUILDING]を入力すると、配信の時に予約のイベント名に変換されます。\n※メッセージに[NAME]を入力すると、配信の時に予約者の名前に変換されます。\n※メッセージに[DATE]を入力すると、配信の時に予約の日付に変換されます。\n※メッセージに[TEL]を入力すると、配信の時に予約者の電話番号に変換されます。\n`}
								</p>
							</motion.div>
							<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
								<Form.Item
									name="messageRegisteredLineWorks"
									label={
										<div className="font-bold text-[#262626]">
											新規予約が登録されると送信されるメッセージ
										</div>
									}
									rules={[
										{
											max: 500,
											message:
												"新規予約が登録されると送信されるメッセージは500文字以内にしてください",
										},
									]}
								>
									<TextArea
										placeholder="メッセージを入力してください"
										autoSize
									/>
								</Form.Item>
							</motion.div>
							<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
								<Form.Item
									name="messageCanceledLineWorks"
									label={
										<div className="font-bold text-[#262626]">
											予約がキャンセルされると送信されるメッセージ
										</div>
									}
									rules={[
										{
											max: 500,
											message:
												"予約がキャンセルされると送信されるメッセージは500文字以内にしてください",
										},
									]}
								>
									<TextArea
										placeholder="メッセージを入力してください"
										autoSize
									/>
								</Form.Item>
							</motion.div>

							<TagInput
								ref={tagInputRef}
								tags={emailsLineWorks}
								setTags={setEmailsLineWorks}
							/>

							{/* <EventDetails /> */}
							{/* <Divider /> */}

							{/* <motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
                <p className="font-bold">イベントプログラムメッセージ</p>
                {!currentOccasion
                  ? currentCategory?.categoryMessages?.map((item, index) => (
                      <FormItem
                        key={item?.showOrder}
                        name={`label${index}`}
                        valuePropName="checked"
                      >
                        <Checkbox>{item?.label}</Checkbox>
                      </FormItem>
                    ))
                  : currentOccasion?.categoryMessages?.map((item, index) => (
                      <FormItem
                        key={item?.showOrder}
                        name={`label${index}`}
                        valuePropName="checked"
                      >
                        <Checkbox>{item?.label}</Checkbox>
                      </FormItem>
                    ))}
              </motion.div> */}
							<Divider />
							<motion.div
								variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
								className="flex justify-center"
							>
								<TapAnimationComponent>
									<Button
										type="primary"
										loading={
											currentOccasion
												? updateOccasionMutation.isLoading
												: createOccasionMutation.isLoading
										}
										onClick={handleOccasion}
									>
										{currentOccasion ? "保存する" : "作成する"}
									</Button>
								</TapAnimationComponent>
							</motion.div>
						</motion.div>
					</Form>
				</div>
			</Modal>
		</>
	);
};

export default OccasionModal;
